import React, { useEffect, useState, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { DialogsProvider, ToastProvider } from '../../../providers';
import { withContextProvider } from '../../../hoc';
import { useDialogs, useToasts } from '../../../hooks';
import Button from '../../../componentsV2/Button/Button';
import TableEditable from '../../../componentsV2/table/TableEditable';
import {
  createProduct, getProducts, removeProduct, updateProduct,
} from '../../../utils/api/products';
import { Plus } from '../../../componentsV2/Icons/Icons';
import Pagination from '../../../componentsV2/pagination/Pagination';
import Breadcrumbs from '../../../componentsV2/Breadcrumbs/Breadcrumbs';
import PagesHeader from '../../../componentsV2/Headers/PagesHeader';
import { getRoleChecks } from '../../../utils/roles';

const ItemsForDistributionPage = ({ currentUserRole, organization }) => {
  const orgId = organization.id;
  const { showToast } = useToasts();
  const itemsPerPage = 20;
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isShowingLineForAdding, setIsShowingLineForAdding] = useState(false);
  const [errors, setErrors] = useState({});
  const [params, setParams] = useState({
    limit: itemsPerPage,
    offset: 0,
  });
  const { openDialog, closeDialog } = useDialogs();
  const { isManager } = getRoleChecks(currentUserRole);

  const fetchProducts = useCallback(() => {
    getProducts(orgId, params)
      .then((response) => response.json())
      .then((data) => {
        setItems(Array.isArray(data?.products) ? data.products : []);
        setTotalItems(Number.isInteger(data?.total_count) ? data.total_count : 0);
      })
      .catch((error) => showToast({ message: error.message, type: 'error' }));
  }, [orgId, params, showToast]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const checkForDuplicateName = (name, excludeId = null) => {
    const trimmedName = name.trim().toLowerCase();
    return items.some((item) => item.name.trim().toLowerCase() === trimmedName && item.id !== excludeId);
  };

  const validateProductName = (name, excludeId = null) => {
    const trimmedName = name ? name.trim() : '';

    if (!trimmedName) {
      setErrors({ name: 'Category must be at least 1 character long.' });
      return null;
    }

    if (checkForDuplicateName(trimmedName, excludeId)) {
      setErrors({ name: 'A category with this label already exists.' });
      return null;
    }

    return trimmedName;
  };

  const handleUpdate = (updatedData, successCallBack) => {
    const trimmedName = validateProductName(updatedData.name, updatedData.id);
    if (!trimmedName) return;

    const dataToUpdate = { ...updatedData, name: trimmedName };

    updateProduct(dataToUpdate, orgId, updatedData.id)
      .then((response) => {
        if (response.ok) {
          successCallBack();
          showToast({ message: 'Category updated successfully.', type: 'success' });
          return response.json();
        }
        showToast({ message: 'Category was not saved.', type: 'error' });
        return null;
      })
      .then((data) => {
        if (data) {
          setItems(prevItems => prevItems.map((item) => (item.id === data.product.id ? data.product : item)));
        }
      })
      .catch(error => showToast({ message: error.message, type: 'error' }));
  };

  const handleCreate = (body, successCallBack = () => {}) => {
    const trimmedName = validateProductName(body.name);
    if (!trimmedName) return;

    const dataToCreate = { ...body, name: trimmedName };

    createProduct(dataToCreate, orgId)
      .then((resp) => {
        if (resp.ok) {
          successCallBack();
          showToast({ message: 'Category added.', type: 'success' });
          fetchProducts();
        } else {
          showToast({ message: 'Category was not saved.', type: 'error' });
        }
      })
      .catch(error => showToast({ message: error.message, type: 'error' }));
  };

  const handleDelete = (id) => {
    removeProduct(id, orgId)
      .then((resp) => {
        if (resp.status === 200) {
          showToast({ message: 'Category has been deleted.', type: 'success' });
          setItems(prevItems => prevItems.filter((item) => item.id !== id));
          fetchProducts();
        } else {
          resp.json().then((data) => {
            const errorMsg = data && data.error ? data.error : 'Category could not be deleted.';
            showToast({ message: errorMsg, type: 'error' });
          });
        }
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
  };

  const deleteItemModal = (id) => {
    openDialog({
      message: [
        <p className="text-center leading-snug">
          Delete <b>{items.find((item) => item.id === id)?.name}</b> category?
        </p>,
      ],
      show: true,
      actions: [
        {
          label: 'Cancel',
          variant: 'secondary',
          onClick: closeDialog,
        },
        {
          label: 'Delete',
          variant: 'primary',
          onClick: () => {
            handleDelete(id);
            closeDialog();
          },
        },
      ],
    });
  };

  const onPageChange = (page) => {
    setCurrentPage(page);
    setParams((prev) => ({ ...prev, offset: (page - 1) * itemsPerPage }));
  };

  const handleProductClick = (product) => {
    if (!product?.id) return;

    const query = new URLSearchParams({
      organization_id: orgId,
      product_id: product.id,
    }).toString();

    window.location.href = `/visits?${query}`;
  };

  const columns = [
    {
      title: 'Category',
      dataIndex: 'name',
      className: 'flex-1',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      className: 'flex-1',
    },
    {
      title: 'Visits',
      dataIndex: 'visits_count',
      className: 'flex-1',
      editable: false,
    },
  ];

  const breadcrumbLinks = [
    { href: '/admin', name: 'Admin' },
    { href: '/admin/items_for_distribution', name: 'Items for Distribution' },
  ];

  return (
    <div className="mx-2.75">
      <header>
        {/* Breadcrumbs */}
        <Breadcrumbs links={breadcrumbLinks} />
      </header>
      <div className="flex w-full justify-between items-center pb-4">
        <PagesHeader name="volunteer_activism" label="Items For Distribution" size="h-10" />
        <Button
          className="justify-center"
          label="Category"
          onClick={() => {
            setIsShowingLineForAdding(true);
          }}
          Icon={Plus}
        />
      </div>
      <TableEditable
        typeOfData="items_for_distribution"
        dataSource={items}
        columns={columns}
        handleUpdate={handleUpdate}
        handleDelete={deleteItemModal}
        handleAdd={handleCreate}
        isShowingLineForAdding={isShowingLineForAdding}
        setIsShowingLineForAdding={setIsShowingLineForAdding}
        defaultNewItem={{ name: '', description: '' }}
        errors={errors}
        setErrors={setErrors}
        isManager={isManager}
        onClick={handleProductClick}
        isRowClickable={(record) => record.visits_count > 0}
      />
      <Pagination
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        currentPage={currentPage}
        setCurrentPage={onPageChange}
        marginTop="my-9"
      />
    </div>
  );
};

// Prop types for component props validation
ItemsForDistributionPage.propTypes = {
  currentUserRole: PropTypes.string.isRequired,
  organization: PropTypes.object.isRequired,
};

export default withContextProvider(DialogsProvider)(withContextProvider(ToastProvider)(ItemsForDistributionPage));
